import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import classNames from 'classnames';
import { isNil, isEmpty, length, startsWith, path } from 'ramda';

import Tooltip, { TOP, LEFT } from '../tooltip/Tooltip.jsx';
import SimpleCheckbox from '../simple-checkbox/SimpleCheckbox.jsx';
import Spinner from '../spinner/Spinner.jsx';

import s from '../../styles/reactTableCustomization.module.scss';

window.selectedPOIS = [];

const PAGE_SIZE = 300;

const rowRenderer = row => (
    <div className={s.cellWrapper} data-testid={'poi-row'}>
        <span className={s.textLimiter}>{row.value}</span>
        <Tooltip text={row.value} arrowPosition={TOP} />
    </div>
);

export default class PoiList extends PureComponent {
    static propTypes = {
        bulkEditIds: PropTypes.shape({ id: PropTypes.string }),
        className: PropTypes.string,
        currentPoiId: PropTypes.string,
        hasLoaded: PropTypes.bool,
        isAllSelected: PropTypes.bool,
        loading: PropTypes.bool,
        onPoiSelect: PropTypes.func,
        poiSaveStatus: PropTypes.objectOf(PropTypes.string),
        poisToRender: PropTypes.arrayOf(PropTypes.object),
        toggleBulkEditId: PropTypes.func,
        toggleSelectAllId: PropTypes.func,
        poiSearchText: PropTypes.string,
        maxLengthSelectAll: PropTypes.number.isRequired,
    };

    static defaultProps = {
        bulkEditIds: { id: '' },
        className: undefined,
        currentPoiId: undefined,
        hasLoaded: false,
        isAllSelected: false,
        loading: false,
        onPoiSelect: () => {},
        poiSaveStatus: {},
        poisToRender: [],
        toggleBulkEditId: () => {},
        toggleSelectAllId: () => {},
        poiSearchText: '',
    };

    POI_COLUMNS = () => {
        const {
            bulkEditIds,
            toggleBulkEditId,
            toggleSelectAllId,
            isAllSelected,
            poiSearchText,
            poisToRender,
            maxLengthSelectAll,
        } = this.props;
        const hideSelectAll =
            isNil(poiSearchText) || isEmpty(poiSearchText) || length(poisToRender) > maxLengthSelectAll;

        return [
            {
                Header: () =>
                    hideSelectAll ? (
                        <div />
                    ) : (
                        <SimpleCheckbox
                            dataCy="bulk-poi-checkbox"
                            data-testid="bulk-poi-checkbox"
                            onChange={() => toggleSelectAllId()}
                            checked={isAllSelected}
                        />
                    ),
                id: 'bulkSelect',
                accessor: value => !!bulkEditIds[value.uuid],
                width: 50,
                Cell: row => (
                    <SimpleCheckbox
                        onChange={() => toggleBulkEditId(row.original.uuid)}
                        checked={row.value}
                        status={row.row.status}
                        disabled={row.row.status === 'ARCHIVED'}
                    />
                ),
            },
            {
                Header: 'ID',
                id: 'id',
                accessor: value => Number(value.id.replace(/#/g, '')),
                width: 70,
                Cell: this.idRowRenderer,
            },
            {
                Header: 'NAME',
                accessor: 'name',
                width: 255,
                Cell: rowRenderer,
            },
            {
                Header: 'CATEGORY',
                accessor: 'category',
                width: 160,
                Cell: rowRenderer,
            },
            {
                Header: 'STATUS',
                id: 'status',
                accessor: value => (startsWith('#', value.id) ? 'ARCHIVED' : 'CURRENT'),
                width: 100,
                Cell: row => (
                    <span className={classNames(s.statusCell, s[row.value])}>
                        {row.value}
                        <Tooltip text={row.value} arrowPosition={TOP} />
                    </span>
                ),
            },
            {
                Header: 'LOCATION',
                accessor: 'gate',
                width: 130,
                Cell: rowRenderer,
            },
            {
                Header: 'LEVEL',
                accessor: 'floor',
                width: 200,
                Cell: rowRenderer,
            },
            {
                Header: 'BUILDING',
                accessor: 'terminal',
                width: 200,
                Cell: rowRenderer,
            },
        ];
    };

    getTrProps = (tableState, rowInfo) => {
        const { currentPoiId } = this.props;
        const result = {};

        if (path(['original', 'uuid'], rowInfo) === currentPoiId) {
            result['data-selected'] = true;
        }
        if (path(['row', 'status'], rowInfo) === 'ARCHIVED') {
            result['data-archived'] = true;
        }
        return result;
    };

    idRowRenderer = tableState => {
        const { poiSaveStatus } = this.props;
        const thisRowSaveStatus = poiSaveStatus[tableState.original.uuid];
        const isSaving = thisRowSaveStatus === 'SAVING';
        const saveError = thisRowSaveStatus === 'ERROR' ? 'Error saving POI' : '';
        const tooltipMessage = isSaving ? 'Updates saving...' : saveError;
        return (
            <span className={classNames(s.idCell, { [s.isSaving]: isSaving }, { [s.hasSaveError]: saveError })}>
                {tableState.value}
                {tooltipMessage && (
                    <Tooltip
                        className={s.tooltip}
                        text={tooltipMessage}
                        arrowPosition={LEFT}
                        type={isSaving ? 'alert' : 'error'}
                    />
                )}
            </span>
        );
    };

    renderErrorMessage = (displayMessage, hasLoaded) => {
        if (displayMessage && hasLoaded) {
            return <div className={s.errorMessage}>No POIs found</div>;
        }
    };

    render() {
        const classes = ['-striped', '-highlight', s.venueList];
        const { poisToRender, loading, className, onPoiSelect, hasLoaded } = this.props;
        const needsPagination = poisToRender.length > PAGE_SIZE;
        const noData = !loading && poisToRender.length === 0;
        return (
            <div className={className}>
                <Spinner loading={loading} />
                <ReactTable
                    data={poisToRender}
                    defaultPageSize={PAGE_SIZE}
                    className={classNames(classes)}
                    columns={this.POI_COLUMNS()}
                    loading={loading}
                    loadingText="Loading POIs"
                    getTdProps={(tableState, rowInfo) => ({ onClick: () => onPoiSelect(rowInfo.original.uuid) })}
                    getTrProps={this.getTrProps}
                    showPageSizeOptions={false}
                    showPagination={needsPagination}
                    previousText="<"
                    nextText=">"
                    minRows={0}
                />
                {this.renderErrorMessage(noData, hasLoaded)}
            </div>
        );
    }
}
